import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { 
  ref, 
  push, 
  onValue, 
  off, 
  onDisconnect, 
  set, 
  remove,
  query,
  orderByChild,
  limitToLast
} from 'firebase/database';
import { realtimeDb } from '../config/firebase';
import { useAuth } from './AuthContext';

interface Message {
  id: string;
  text: string;
  userId: string;
  username: string;
  timestamp: number;
  type: 'text' | 'system';
  isAdmin?: boolean;
}

interface OnlineUser {
  userId: string;
  username: string;
  lastSeen: number;
  status: 'online' | 'away' | 'offline';
}

interface ChatContextType {
  messages: Message[];
  onlineUsers: OnlineUser[];
  isConnected: boolean;
  unreadCount: number;
  sendMessage: (text: string) => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  setTyping: (isTyping: boolean) => void;
  typingUsers: string[];
  markAsRead: () => void;
}

const ChatContext = createContext<ChatContextType>({} as ChatContextType);

export const useChat = () => {
  return useContext(ChatContext);
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const { currentUser, userData } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadMessageId, setLastReadMessageId] = useState<string | null>(null);

  // Mesajları dinle
  useEffect(() => {
    if (!currentUser || !userData) return;

    const messagesRef = query(
      ref(realtimeDb, 'messages'),
      orderByChild('timestamp'),
      limitToLast(50)
    );

    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const messagesData: Message[] = [];
      snapshot.forEach((childSnapshot) => {
        const messageData = childSnapshot.val();
        messagesData.push({
          id: childSnapshot.key!,
          ...messageData
        });
      });
      setMessages(messagesData);
      
      // Okunmamış mesaj sayısını hesapla
      if (currentUser) {
        const unreadMessages = messagesData.filter(msg => 
          msg.userId !== currentUser.uid && 
          (!lastReadMessageId || msg.id > lastReadMessageId)
        );
        setUnreadCount(unreadMessages.length);
      }
    });

    return () => {
      off(messagesRef, 'value', unsubscribe);
    };
  }, [currentUser, userData, lastReadMessageId]);

  // Online kullanıcıları dinle (sadece giriş yapmış kullanıcılar için)
  useEffect(() => {
    if (!currentUser || !userData) {
      // Kullanıcı giriş yapmamışsa online users listesini temizle
      setOnlineUsers([]);
      return;
    }

    const onlineUsersRef = ref(realtimeDb, 'onlineUsers');
    
    onValue(onlineUsersRef, (snapshot) => {
      const usersData: OnlineUser[] = [];
      const data = snapshot.val();
      
      if (data) {
        Object.keys(data).forEach((userId) => {
          const userData = data[userId];
          // Sadece online kullanıcıları ekle
          if (userData && userData.status === 'online') {
            usersData.push({
              userId: userId,
              ...userData
            });
          }
        });
      }
      
      setOnlineUsers(usersData);
    }, (error) => {
      console.error('Online users listener hatası:', error);
    });

    return () => {
      off(onlineUsersRef);
    };
  }, [currentUser, userData]); // currentUser ve userData dependency'sini geri ekledik

  // Kullanıcıyı online olarak işaretle
  useEffect(() => {
    if (!currentUser || !userData) {
      return;
    }

    const userStatusRef = ref(realtimeDb, `onlineUsers/${currentUser.uid}`);
    const userStatusData = {
      userId: currentUser.uid,
      username: userData.displayName || userData.username || currentUser.displayName || 'Bilinmeyen Kullanıcı',
      lastSeen: Date.now(),
      status: 'online'
    };

    // Kullanıcıyı online olarak ayarla
    set(userStatusRef, userStatusData).catch((error) => {
      console.error('Online status ayarlama hatası:', error);
    });

    // Bağlantı kesildiğinde offline yap
    onDisconnect(userStatusRef).set({
      ...userStatusData,
      status: 'offline',
      lastSeen: Date.now()
    });

    // Bağlantı durumunu kontrol et
    const connectedRef = ref(realtimeDb, '.info/connected');
    const connectedUnsubscribe = onValue(connectedRef, (snapshot) => {
      const connected = snapshot.val() === true;
      setIsConnected(connected);
    });

    return () => {
      // Kullanıcı çıkış yaparken online status'u tamamen kaldır
      remove(userStatusRef);
      off(connectedRef, 'value', connectedUnsubscribe);
    };
  }, [currentUser, userData]);


  // Yazıyor göstergesini dinle
  useEffect(() => {
    if (!currentUser) return;

    const typingRef = ref(realtimeDb, 'typing');
    
    const unsubscribe = onValue(typingRef, (snapshot) => {
      const typingData = snapshot.val();
      if (typingData) {
        const typingUserIds = Object.keys(typingData).filter(
          userId => userId !== currentUser.uid && typingData[userId]
        );
        setTypingUsers(typingUserIds);
      } else {
        setTypingUsers([]);
      }
    });

    return () => {
      off(typingRef, 'value', unsubscribe);
    };
  }, [currentUser]);

  // Mesaj gönder
  const sendMessage = async (text: string) => {
    if (!currentUser || !userData || !text.trim()) {
      return;
    }

    try {
      const messagesRef = ref(realtimeDb, 'messages');
      const newMessageRef = push(messagesRef);
      
      const messageData = {
        text: text.trim(),
        userId: currentUser.uid,
        username: userData.username,
        timestamp: Date.now(),
        type: 'text',
        isAdmin: userData.role === 'admin'
      };

      await set(newMessageRef, messageData);
    } catch (error) {
      console.error('Mesaj gönderilemedi:', error);
    }
  };

  // Yazıyor göstergesi
  const setTyping = (isTyping: boolean) => {
    if (!currentUser) return;

    const typingRef = ref(realtimeDb, `typing/${currentUser.uid}`);
    
    if (isTyping) {
      set(typingRef, true);
      // 3 saniye sonra otomatik olarak kaldır
      setTimeout(() => {
        remove(typingRef);
      }, 3000);
    } else {
      remove(typingRef);
    }
  };

  // Mesaj silme (sadece admin)
  const deleteMessage = async (messageId: string) => {
    if (!currentUser || !userData || userData.role !== 'admin') {
      return;
    }

    try {
      const messageRef = ref(realtimeDb, `messages/${messageId}`);
      await remove(messageRef);
      
      // State'i manuel olarak güncelle
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== messageId));
      
    } catch (error) {
      console.error('Mesaj silinemedi:', error);
    }
  };

  // Mesajları okundu olarak işaretle
  const markAsRead = () => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      setLastReadMessageId(lastMessage.id);
      setUnreadCount(0);
    }
  };

  const value = {
    messages,
    onlineUsers,
    isConnected,
    unreadCount,
    sendMessage,
    deleteMessage,
    setTyping,
    typingUsers,
    markAsRead
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};
